import { Link } from "react-router-dom";
import { server } from "../../server";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { getDecodedToken } from "../../auth";

function StaffAttendance() {
  const user = getDecodedToken();
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [summary, setSummary] = useState({ total: 0, present: 0, absent: 0 });
  const [loading, setLoading] = useState(true);
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  useEffect(() => {
    axios
      .post(
        `${server}/staffAttendanceByDate`,
        { date: today },
        { headers: { Authorization: `${localStorage.getItem("token")}` } },
      )
      .then((response) => {
        const apiData = response.data?.["body-json"];
        if (apiData?.statusCode === 200) {
          let parsedBody = apiData.body;
          if (typeof parsedBody === "string") {
            try {
              parsedBody = JSON.parse(parsedBody);
            } catch {
              parsedBody = {};
            }
          }
          const attendanceList = parsedBody?.data ?? [];
          setStaffAttendance(
            Array.isArray(attendanceList) ? attendanceList : [],
          );
          if (parsedBody?.summary) setSummary(parsedBody.summary);
        } else {
          setStaffAttendance([]);
        }
      })
      .catch((err) => console.error("API Error:", err))
      .finally(() => setLoading(false));
  }, [today]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  const isPresent = (staff) => staff?.attendance?.in?.status === "Present";

  const buildSheet = (headers, rows, colWidths) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = colWidths;
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    return ws;
  };

  const baseHeaders = [
    "No.",
    "Staff ID",
    "Full Name",
    "Category",
    "Designation",
    "Department",
    "Class Teacher",
    "Teacher Type",
  ];

  const baseColWidths = [
    { wch: 5 },
    { wch: 15 },
    { wch: 25 },
    { wch: 16 },
    { wch: 18 },
    { wch: 22 },
    { wch: 14 },
    { wch: 12 },
  ];

  const toBaseRow = (staff, i) => [
    i + 1,
    staff?.staffId || "—",
    staff?.name || "—",
    staff?.categoryDetails?.staffCategory || "—",
    staff?.categoryDetails?.designation || "—",
    staff?.categoryDetails?.department || "—",
    staff?.categoryDetails?.classTeacher || "—",
    staff?.categoryDetails?.teacherType || "—",
  ];

  // mode: "all" | "in" | "out"
  const exportSheet = (data, mode, sheetName, fileName) => {
    if (!data.length) {
      toast.warn("No data to export.");
      return;
    }

    let headers, colWidths, rows;

    if (mode === "all") {
      headers = [
        ...baseHeaders,
        "IN Status",
        "IN Time",
        "IN Document",
        "OUT Status",
        "OUT Time",
        "OUT Document",
      ];
      colWidths = [
        ...baseColWidths,
        { wch: 12 },
        { wch: 14 },
        { wch: 60 },
        { wch: 12 },
        { wch: 14 },
        { wch: 60 },
      ];
      rows = data.map((s, i) => [
        ...toBaseRow(s, i),
        s?.attendance?.in?.status || "Absent",
        s?.attendance?.in?.time || "—",
        s?.attendance?.in?.document || "—",
        s?.attendance?.out?.status || "Absent",
        s?.attendance?.out?.time || "—",
        s?.attendance?.out?.document || "—",
      ]);
    } else if (mode === "in") {
      headers = [...baseHeaders, "IN Status", "IN Time", "IN Document"];
      colWidths = [...baseColWidths, { wch: 12 }, { wch: 14 }, { wch: 60 }];
      rows = data.map((s, i) => [
        ...toBaseRow(s, i),
        s?.attendance?.in?.status || "Absent",
        s?.attendance?.in?.time || "—",
        s?.attendance?.in?.document || "—",
      ]);
    } else {
      headers = [...baseHeaders, "OUT Status", "OUT Time", "OUT Document"];
      colWidths = [...baseColWidths, { wch: 12 }, { wch: 14 }, { wch: 60 }];
      rows = data.map((s, i) => [
        ...toBaseRow(s, i),
        s?.attendance?.out?.status || "Absent",
        s?.attendance?.out?.time || "—",
        s?.attendance?.out?.document || "—",
      ]);
    }

    const ws = buildSheet(headers, rows, colWidths);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}_${today}.xlsx`);
    toast.success(`${sheetName} exported!`);
  };

  // ── Export handlers ───────────────────────────────────────────────────────────

  const exportAllAttendance = () =>
    exportSheet(
      staffAttendance,
      "all",
      "All Attendance",
      "All_Staff_Attendance",
    );

  const exportPresentAttendance = () =>
    exportSheet(
      staffAttendance.filter(isPresent),
      "in",
      "Present Staff",
      "Present_Staff",
    );

  const exportAbsentAttendance = () =>
    exportSheet(
      staffAttendance.filter((s) => !isPresent(s)),
      "all",
      "Absent Staff",
      "Absent_Staff",
    );

  const exportInAttendance = () =>
    exportSheet(
      staffAttendance.filter((s) => s?.attendance?.in?.status === "Present"),
      "in",
      "IN Attendance",
      "IN_Attendance",
    );

  const exportOutAttendance = () =>
    exportSheet(
      staffAttendance.filter((s) => s?.attendance?.out?.status === "Present"),
      "out",
      "OUT Attendance",
      "OUT_Attendance",
    );

  const exportByCategorywise = (category) =>
    exportSheet(
      staffAttendance.filter(
        (s) =>
          s?.categoryDetails?.staffCategory?.toLowerCase() ===
          category.toLowerCase(),
      ),
      "all",
      `${category} Staff`,
      `${category.replace(/\s+/g, "_")}_Attendance`,
    );

  const exportByTeacherType = (type) =>
    exportSheet(
      staffAttendance.filter(
        (s) =>
          s?.categoryDetails?.teacherType?.toLowerCase() === type.toLowerCase(),
      ),
      "all",
      `${type} Teachers`,
      `${type}_Attendance`,
    );

  // Department-wise → one sheet per department in a single workbook
  const exportByDepartment = () => {
    if (!staffAttendance.length) {
      toast.warn("No data to export.");
      return;
    }

    const deptMap = {};
    staffAttendance.forEach((s) => {
      const dept = s?.categoryDetails?.department || "Unknown";
      if (!deptMap[dept]) deptMap[dept] = [];
      deptMap[dept].push(s);
    });

    const headers = [
      ...baseHeaders,
      "IN Status",
      "IN Time",
      "OUT Status",
      "OUT Time",
    ];
    const colWidths = [
      ...baseColWidths,
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    Object.entries(deptMap).forEach(([dept, records]) => {
      const rows = records.map((s, i) => [
        ...toBaseRow(s, i),
        s?.attendance?.in?.status || "Absent",
        s?.attendance?.in?.time || "—",
        s?.attendance?.out?.status || "Absent",
        s?.attendance?.out?.time || "—",
      ]);
      XLSX.utils.book_append_sheet(
        wb,
        buildSheet(headers, rows, colWidths),
        dept.replace(/[:\\/?*[\]]/g, "_").substring(0, 31),
      );
    });

    XLSX.writeFile(wb, `Department_Wise_Attendance_${today}.xlsx`);
    toast.success("Department-wise report exported!");
  };

  // Dynamically derive unique teacher types from loaded data
  const teacherTypes = [
    ...new Set(
      staffAttendance
        .map((s) => s?.categoryDetails?.teacherType)
        .filter(Boolean),
    ),
  ];

  // Category-wise → one sheet per category in a single workbook
  const exportByCategoryWiseReport = () => {
    if (!staffAttendance.length) {
      toast.warn("No data to export.");
      return;
    }

    const categoryMap = {};
    staffAttendance.forEach((s) => {
      const category = s?.categoryDetails?.staffCategory || "Unknown";
      if (!categoryMap[category]) categoryMap[category] = [];
      categoryMap[category].push(s);
    });

    const headers = [
      ...baseHeaders,
      "IN Status",
      "IN Time",
      "OUT Status",
      "OUT Time",
    ];
    const colWidths = [
      ...baseColWidths,
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    Object.entries(categoryMap).forEach(([category, records]) => {
      const rows = records.map((s, i) => [
        ...toBaseRow(s, i),
        s?.attendance?.in?.status || "Absent",
        s?.attendance?.in?.time || "—",
        s?.attendance?.out?.status || "Absent",
        s?.attendance?.out?.time || "—",
      ]);
      XLSX.utils.book_append_sheet(
        wb,
        buildSheet(headers, rows, colWidths),
        category.replace(/[:\\/?*[\]]/g, "_").substring(0, 31),
      );
    });

    XLSX.writeFile(wb, `Category_Wise_Attendance_${today}.xlsx`);
    toast.success("Report Generated");
  };

  // ─── Filter & Pagination ──────────────────────────────────────────────────────

  const filteredRecords = staffAttendance.filter((staff) => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(staff).some((value) => {
      if (typeof value === "string")
        return value.toLowerCase().includes(searchLower);
      if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (sub) =>
            typeof sub === "string" && sub.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  filteredRecords.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // ─── Range Report Download ────────────────────────────────────────────────────

  const downloadRangeReport = async () => {
    const fromDate = fromDateInputRef.current?.value;
    const toDate = toDateInputRef.current?.value;

    if (!fromDate || !toDate) {
      toast.warn("Please Both From and To Dates");
      return;
    }
    if (fromDate > toDate) {
      toast.warn("From Date must be before To Date.");
      return;
    }

    try {
      const res = await axios.post(
        `${server}/staffAttendanceByDate`,
        { fromDate, toDate },
        { headers: { Authorization: `${localStorage.getItem("token")}` } },
      );

      const apiData = res.data?.["body-json"];
      if (apiData?.statusCode !== 200) {
        toast.warn("No data returned from server.");
        return;
      }

      let body = apiData.body;
      if (typeof body === "string") {
        try {
          body = JSON.parse(body);
        } catch {
          body = {};
        }
      }

      const staffList = Array.isArray(body?.data) ? body.data : [];
      if (!staffList.length) {
        toast.warn("No attendance data found for selected range.");
        return;
      }

      // ── Collect all unique dates across all staff attendance arrays ──────────
      const dateSet = new Set();
      staffList.forEach((staff) => {
        (staff.attendance || []).forEach(({ date }) => {
          if (date) dateSet.add(date);
        });
      });
      const dates = [...dateSet].sort(); // ascending

      // ── Build lookup: staffId → { meta, byDate: { "YYYY-MM-DD": { in, out } } }
      // ── Build lookup ─────────────────────────────────────────────────────────────
      const staffMap = staffList
        .map((staff) => {
          const byDate = {};
          (staff.attendance || []).forEach(
            ({ date, in: inData, out: outData }) => {
              byDate[date] = {
                in: inData?.status === "Present" ? "P" : "A",
                inTime: inData?.time || "—",
                out: outData?.status === "Present" ? "P" : "A",
                outTime: outData?.time || "—",
              };
            },
          );
          return {
            staffId: staff?.staffId || "—",
            name: staff?.name || "—",
            category: staff?.categoryDetails?.staffCategory || "—",
            designation: staff?.categoryDetails?.designation || "—",
            department: staff?.categoryDetails?.department || "—",
            byDate,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      // ── Build Excel ───────────────────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      const ws = {};

      const fixedHeaders = [
        "No.",
        "Staff ID",
        "Full Name",
        "Category",
        "Designation",
        "Department",
      ];

      // Row 1 — merged title
      ws["A1"] = {
        v: `Staff Attendance Register: ${fromDate} to ${toDate}`,
        t: "s",
      };

      // Row 2 — column headers (4 cols per date: IN | IN Time | OUT | OUT Time)
      fixedHeaders.forEach((h, ci) => {
        ws[XLSX.utils.encode_cell({ r: 1, c: ci })] = { v: h, t: "s" };
      });

      dates.forEach((date, di) => {
        const base = fixedHeaders.length + di * 4;
        const dateObj = new Date(date);
        const dayName = dateObj.toLocaleDateString("en-GB", {
          weekday: "short",
        });
        const label = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
        });
        const prefix = `${dayName} ${label}`;

        ws[XLSX.utils.encode_cell({ r: 1, c: base })] = {
          v: `${prefix} IN`,
          t: "s",
        };
        ws[XLSX.utils.encode_cell({ r: 1, c: base + 1 })] = {
          v: `${prefix} IN Time`,
          t: "s",
        };
        ws[XLSX.utils.encode_cell({ r: 1, c: base + 2 })] = {
          v: `${prefix} OUT`,
          t: "s",
        };
        ws[XLSX.utils.encode_cell({ r: 1, c: base + 3 })] = {
          v: `${prefix} OUT Time`,
          t: "s",
        };
      });

      // Rows 3+ — staff data
      staffMap.forEach((staff, si) => {
        const rowIdx = 2 + si;
        [
          si + 1,
          staff.staffId,
          staff.name,
          staff.category,
          staff.designation,
          staff.department,
        ].forEach((val, ci) => {
          ws[XLSX.utils.encode_cell({ r: rowIdx, c: ci })] = {
            v: val,
            t: typeof val === "number" ? "n" : "s",
          };
        });

        dates.forEach((date, di) => {
          const base = fixedHeaders.length + di * 4;
          const dayData = staff.byDate[date] || {
            in: "A",
            inTime: "—",
            out: "A",
            outTime: "—",
          };

          ws[XLSX.utils.encode_cell({ r: rowIdx, c: base })] = {
            v: dayData.in,
            t: "s",
          };
          ws[XLSX.utils.encode_cell({ r: rowIdx, c: base + 1 })] = {
            v: dayData.inTime,
            t: "s",
          };
          ws[XLSX.utils.encode_cell({ r: rowIdx, c: base + 2 })] = {
            v: dayData.out,
            t: "s",
          };
          ws[XLSX.utils.encode_cell({ r: rowIdx, c: base + 3 })] = {
            v: dayData.outTime,
            t: "s",
          };
        });
      });

      // Sheet metadata
      const totalCols = fixedHeaders.length + dates.length * 4;
      const totalRows = 2 + staffMap.length;
      ws["!ref"] = XLSX.utils.encode_range(
        { r: 0, c: 0 },
        { r: totalRows - 1, c: totalCols - 1 },
      );
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } }];
      ws["!cols"] = [
        { wch: 5 },
        { wch: 14 },
        { wch: 26 },
        { wch: 16 },
        { wch: 18 },
        { wch: 20 },
        ...dates.flatMap(() => [
          { wch: 6 },
          { wch: 13 },
          { wch: 6 },
          { wch: 13 },
        ]),
      ];
      ws["!freeze"] = { xSplit: 6, ySplit: 2 };

      XLSX.utils.book_append_sheet(wb, ws, "Attendance Register");
      XLSX.writeFile(wb, `Staff_Attendance_${fromDate}_to_${toDate}.xlsx`);
      toast.success("Report Generated");
    } catch (err) {
      toast.error("Failed To Generate Report");
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                {/* ── Table ── */}
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((staff, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Staff ID</span>{" "}
                              &mdash; {staff?.staffId}
                              <br />
                              <span className="text-dark">Full Name</span>{" "}
                              &mdash; {staff?.name}
                              <br />
                              <span className="text-dark">Category</span>{" "}
                              &mdash;{" "}
                              {staff?.categoryDetails?.staffCategory || "—"}
                              {staff?.categoryDetails?.teacherType && (
                                <span className="badge bg-light text-dark ms-2 border">
                                  {staff.categoryDetails.teacherType}
                                </span>
                              )}
                              <hr className="w-25" />
                              <span className="text-dark">IN Status</span>{" "}
                              &mdash; {staff.attendance?.in?.status || "—"}
                              <br />
                              <span className="text-dark">
                                Time
                              </span> &mdash;{" "}
                              {staff.attendance?.in?.time || "—"}
                              <br />
                              <span className="text-dark">Document</span>{" "}
                              &mdash;{" "}
                              {staff.attendance?.in?.document ? (
                                <a
                                  href={staff.attendance.in.document}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Click Here
                                </a>
                              ) : (
                                "—"
                              )}
                              <hr className="w-25" />
                              <span className="text-dark">OUT Status</span>{" "}
                              &mdash; {staff.attendance?.out?.status || "—"}
                              <br />
                              <span className="text-dark">
                                Time
                              </span> &mdash;{" "}
                              {staff.attendance?.out?.time || "—"}
                              <br />
                              <span className="text-dark">Document</span>{" "}
                              &mdash;{" "}
                              {staff.attendance?.out?.document ? (
                                <a
                                  href={staff.attendance.out.document}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Click Here
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                {isPresent(staff) ? (
                                  <span className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                    Marked
                                  </span>
                                ) : (
                                  <span className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                    Not Marked
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── Sidebar ── */}
                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                          />
                        </form>
                        <Link
                          to="/add-staff"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Staff
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Human Resource</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Staff Attendance
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" alt="" />
                          )}
                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Staff Attendance
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.5 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>

                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3 d-flex flex-column gap-3">
                              <div className="d-flex flex-wrap gap-1">
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={exportAllAttendance}
                                >
                                  All Staff Attendance
                                </span>
                                <span
                                  className="btn btn-outline-success btn-sm px-3 rounded-pill"
                                  onClick={exportPresentAttendance}
                                >
                                  Present
                                </span>
                                <span
                                  className="btn btn-outline-danger btn-sm px-3 rounded-pill"
                                  onClick={exportAbsentAttendance}
                                >
                                  Absent
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={exportInAttendance}
                                >
                                  IN Attendance
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={exportOutAttendance}
                                >
                                  OUT Attendance
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={() =>
                                    exportByCategorywise("Teaching")
                                  }
                                >
                                  Teaching
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={() =>
                                    exportByCategorywise("Non-Teaching")
                                  }
                                >
                                  Non-Teaching
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={exportByDepartment}
                                >
                                  Department Wise Attendance
                                </span>
                                <span
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                  onClick={exportByCategoryWiseReport}
                                >
                                  Category Wise Attendance
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      <div className="row g-1 mt-1">
                        <div className="col-6">
                          <div className="p-3 rounded-4 bg-info-soft">
                            <h4 className="m-0 text-center lh-1">
                              {summary.total}
                            </h4>
                            <p className="text-center fs-5 m-0 lh-1 mt-1">
                              Total Staff
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="p-3 rounded-4 bg-success-soft">
                            <h4 className="m-0 text-center lh-1">
                              {summary.present}
                            </h4>
                            <p className="text-center fs-5 m-0 lh-1 mt-1">
                              Total Present
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="p-3 rounded-4 bg-danger-soft">
                            <h4 className="m-0 text-center lh-1">
                              {summary.absent}
                            </h4>
                            <p className="text-center fs-5 m-0 lh-1 mt-1">
                              Total Absent
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-secondary-soft rounded-4 py-3 px-4 mt-3">
                        <div>
                          <input
                            type="date"
                            className="form-control ps-4 pe-5 border-0 rounded-pill"
                            placeholder="..."
                            ref={fromDateInputRef}
                          />
                          <input
                            type="date"
                            className="form-control ps-4 pe-5 border-0 rounded-pill mt-1"
                            placeholder="..."
                            ref={toDateInputRef}
                          />
                        </div>
                        <span
                          onClick={downloadRangeReport}
                          className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                        >
                          Download Report
                        </span>
                      </div>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);
                            if (currentPage > 3)
                              pageNumbers.push("left-ellipsis");
                            if (currentPage !== 1 && currentPage !== totalPages)
                              pageNumbers.push(currentPage);
                            if (currentPage < totalPages - 2)
                              pageNumbers.push("right-ellipsis");
                            if (totalPages > 1) pageNumbers.push(totalPages);

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return (
                                <li
                                  key={index}
                                  className={`page-item ${currentPage === page ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default StaffAttendance;
